/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.22.0
 */

import { FC, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Title from '@components/common/Title/Title';
import { EBasketType } from '@modules/app/appConstants';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardOrderReport,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import Wizard from '@modules/wizard/components/Wizard';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';

import { wizardOrderReportHandleNextAction } from '../actions';
import { EWizardOrderReportSteps } from '../constants';
import { makeSelectWizardOrderReportValidation } from '../selectors';
import { IWizardOrderReportLocationData } from '../types';
import WizardOrderReportConfirmStep from './WizardOrderReportConfirmStep';
import WizardOrderReportSelectionDataStep from './WizardOrderReportSelectionDataStep';
import WizardOrderReportSelectionParametersStep from './WizardOrderReportSelectionParametersStep';

const WizardOrderReport: FC = () => {
    const { state } = useLocation<IWizardOrderReportLocationData>();

    const dispatch = useDispatch();

    const tCommon = useSelector(makeSelectTranslationCommon());
    const validation = useSelector(makeSelectWizardOrderReportValidation());
    const stepName = useSelector(makeSelectWizardStepName());

    const wizardTranslation = useSelector(makeSelectTranslationWizardOrderReport());

    const Step = useMemo(() => {
        switch (stepName) {
            case EWizardOrderReportSteps.ReportSelectionDataStep:
                return <WizardOrderReportSelectionDataStep reportData={state} />;
            case EWizardOrderReportSteps.ReportSelectionParametersStep:
                return <WizardOrderReportSelectionParametersStep />;
            case EWizardOrderReportSteps.ReportConfirmStep:
                return <WizardOrderReportConfirmStep />;
        }
    }, [state, stepName]);

    const handleNext = () => dispatch(wizardOrderReportHandleNextAction());

    return (
        <>
            <PageLocale
                lazy
                fileNames={[TranslationFileNamesEnum.wizardOrderReport, TranslationFileNamesEnum.wizard]}
            />
            <Title pageName={ECommonTranslationPages.wizardOrderReport} />
            <Wizard
                basketType={EBasketType.MOBILE}
                isNextDisabled={!validation?.isValid}
                name={EWizardName.ORDER_REPORT}
                translationProps={{
                    error: wizardTranslation.error,
                    steps: wizardTranslation.steps,
                    title: tCommon.pages.wizardOrderReport,
                }}
                validateError={validation?.error}
                onWizardNextHandler={handleNext}
            >
                {Step}
            </Wizard>
        </>
    );
};

export default WizardOrderReport;
